import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { ErrorDisplay } from '@/components/common/error-display'
import {
  CurrentSubscriptionCard,
  PricingCard,
  SubscriptionDetailsCard,
  useBillingPortalUrl,
  useBillingQuery,
  useResumeSubscription,
  useUpdateBillingSeats,
} from '@/features/billing'
import { Skeleton } from '@/components/ui/skeleton'
import { env } from '@/env'
import { AuthGuard, useCurrentOrganizationId } from '@/features/auth'

export const Route = createFileRoute('/(dashboard)/organization/billing/')({
  component: RouteComponent,
  beforeLoad: () => ({
    title: 'Billing & Subscription',
    breadcrumbs: [{ title: 'Organization' }, { title: 'Billing' }],
  }),
})

function RouteComponent() {
  const orgId = useCurrentOrganizationId()
  const [isLoadingPortal, setIsLoadingPortal] = useState(false)

  const billingInfo = useBillingQuery()
  const isLoading = billingInfo === undefined

  const updateQuantityMutation = useUpdateBillingSeats()
  const resumeSubscriptionMutation = useResumeSubscription()
  const getPortalUrl = useBillingPortalUrl()

  const handleBuySeats = (quantity: number) => {
    if (!billingInfo?.organizationId) {
      toast.error('Organization ID not found')
      return
    }

    // If org already has an active subscription, update quantity via action
    if (
      billingInfo.lemonSqueezySubscriptionId &&
      billingInfo.billingStatus === 'active'
    ) {
      const currentPaidSeats = Math.max(0, (billingInfo.employeeLimit || 5) - 5)
      const newPaidSeats = currentPaidSeats + quantity

      void updateQuantityMutation.mutate(newPaidSeats, {
        onSuccess: () => {
          toast.success(
            `Successfully added ${quantity} seat${quantity > 1 ? 's' : ''}! Total: ${5 + newPaidSeats} seats`,
          )
        },
        onError: (err) => {
          toast.error(err.message)
        },
      })
      return
    }

    // For unpaid / expired — redirect to checkout
    const checkoutUrl = `${env.VITE_LEMONSQUEEZY_CHECKOUT_URL}?quantity=${quantity}&checkout[custom][organization_id]=${billingInfo.organizationId}`
    window.location.href = checkoutUrl
  }

  const handleResumeSubscription = () => {
    void resumeSubscriptionMutation.mutate({
      onSuccess: () => {
        toast.success('Subscription resumed successfully!')
      },
      onError: (err) => {
        toast.error(err.message)
      },
    })
  }

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true)
    try {
      const url = await getPortalUrl()
      window.open(url, '_blank')
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setIsLoadingPortal(false)
    }
  }

  // Platform org (NextPeak itself) — no billing required
  if (!isLoading && billingInfo?.isPlatformOrg) {
    return (
      <AuthGuard
        requiredOrganizationRole={{
          organizationId: orgId || '',
          role: ['admin', 'billing'],
        }}
      >
        <div className="space-y-6">
          <div className="p-6 bg-muted/30 rounded-lg">
            <h1 className="text-2xl font-semibold text-foreground">
              Billing & Subscription
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your subscription and billing.
            </p>
          </div>
          <div className="p-6 bg-primary/5 border border-primary/20 rounded-lg text-center space-y-1">
            <p className="font-semibold">Platform Account</p>
            <p className="text-sm text-muted-foreground">
              This organization is not subject to seat limits or billing.
            </p>
          </div>
        </div>
      </AuthGuard>
    )
  }

  return (
    <AuthGuard
      requiredOrganizationRole={{
        organizationId: orgId || '',
        role: ['admin', 'billing'],
      }}
    >
      <div className="space-y-6">
        <div className="p-6 bg-muted/30 rounded-lg">
          <h1 className="text-2xl font-semibold text-foreground">
            Billing & Subscription
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your subscription and billing.
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        ) : !billingInfo ? (
          <ErrorDisplay
            error={new Error('Failed to load billing information')}
            title="Failed to load billing information"
            variant="card"
          />
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <CurrentSubscriptionCard
                billingInfo={billingInfo}
                onBuySeats={handleBuySeats}
                onResumeSubscription={handleResumeSubscription}
                onManageSubscription={handleManageSubscription}
                isResuming={resumeSubscriptionMutation.isPending}
                isBuyingSeats={
                  updateQuantityMutation.isPending || isLoadingPortal
                }
              />
              <PricingCard />
            </div>

            <SubscriptionDetailsCard
              billingInfo={billingInfo}
              onManageSubscription={handleManageSubscription}
              isManaging={isLoadingPortal}
            />
          </>
        )}
      </div>
    </AuthGuard>
  )
}
